import React, { forwardRef, useEffect, useState } from "react";
import "./DateInput.css";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import { IconButton, Tooltip } from "@mui/material";
import { ReactComponent as InfoIcon } from "../../../assets/images/info.svg";

const renderHeading = (heading) => {
  if (typeof heading !== "string") return heading;
  const trimmed = heading.trimEnd();
  if (!trimmed.endsWith("*")) return heading;
  return (
    <>
      {trimmed.slice(0, -1)}
      <span style={{ color: "red" }}>*</span>
    </>
  );
};

const pad2 = (n) => String(n).padStart(2, "0");

const formatDateValue = (date) =>
  date instanceof Date && !isNaN(date.getTime())
    ? `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${date.getFullYear()}`
    : "";

const formatTyped = (raw) => {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  return [dd, mm, yyyy].filter(Boolean).join("-");
};

const parseTyped = (typed) => {
  const match = typed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (
    date.getFullYear() !== Number(yyyy) ||
    date.getMonth() !== Number(mm) - 1 ||
    date.getDate() !== Number(dd)
  ) {
    return null;
  }
  return date;
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const CustomInput = forwardRef(
  (
    {
      selectedDate,
      onClick,
      onFocus,
      onKeyDown,
      placeholder,
      readOnly,
      elemHeight,
      disabled,
      minDate,
      maxDate,
      onDateTyped,
      calendarOpen,
    },
    ref,
  ) => {
    const displayValue = formatDateValue(selectedDate);
    const [typedValue, setTypedValue] = useState(displayValue);

    useEffect(() => {
      setTypedValue(displayValue);
    }, [displayValue]);

    const commitTyped = () => {
      if (!typedValue) {
        onDateTyped(null);
        return;
      }
      const parsed = parseTyped(typedValue);
      if (!parsed) {
        setTypedValue(displayValue);
        return;
      }
      const day = startOfDay(parsed);
      if (minDate && day < startOfDay(minDate)) {
        setTypedValue(displayValue);
        return;
      }
      if (maxDate && day > startOfDay(maxDate)) {
        setTypedValue(displayValue);
        return;
      }
      onDateTyped(parsed);
    };

    return (
      <div
        className={`custom-input ${disabled ? "disabled-input" : ""}`}
        style={{ height: `${elemHeight ? elemHeight : "48"}px` }}
      >
        <div className="input-icon-wrapper">
          <input
            ref={ref}
            type="text"
            readOnly={readOnly || disabled}
            value={typedValue}
            placeholder={placeholder}
            className="input-with-icon"
            style={{ textAlign: typedValue ? "center" : "left" }}
            onClick={!disabled ? onClick : undefined}
            onFocus={!disabled ? onFocus : undefined}
            disabled={disabled}
            onChange={(e) => {
              if (readOnly || disabled) return;
              setTypedValue(formatTyped(e.target.value));
            }}
            onBlur={() => {
              if (readOnly || disabled) return;
              commitTyped();
            }}
            onKeyDown={(e) => {
              if (readOnly || disabled) return;
              if (e.key === "Enter" && !calendarOpen) {
                e.preventDefault();
                commitTyped();
                return;
              }
              onKeyDown?.(e);
            }}
          />
        </div>
      </div>
    );
  },
);

const DateInput = ({
  heading,
  value,
  onChange,
  readOnly,
  minDate,
  maxDate,
  height,
  error,
  disabled,
}) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const selectedDate = value ? new Date(value) : null;
  const normalizedMinDate = minDate ? new Date(minDate) : undefined;
  const normalizedMaxDate = maxDate ? new Date(maxDate) : undefined;
  return (
    <div className="inputDateContainer" style={{ position: "relative" }}>
      {heading && (
        <div className="inputextElem">
          {renderHeading(heading)}
          {(heading === "Terms Expiry Date" ||
            heading === "Listing Expiry Date") &&
            !calendarOpen && (
              <div style={{ position: "relative" }}>
                <Tooltip
                  title="Expiry Date & Time should be at least 3 hours from first slot"
                  style={{
                    position: "absolute",
                    top: "-22px",
                    left: "120px",
                  }}
                >
                  <IconButton>
                    <InfoIcon style={{ width: "14px", height: "16px" }} />
                  </IconButton>
                </Tooltip>
              </div>
            )}
        </div>
      )}
      <div className="date-picker-container">
        <DatePicker
          selected={selectedDate}
          onChange={(date) => onChange(date)}
          onCalendarOpen={() => setCalendarOpen(true)}
          onCalendarClose={() => setCalendarOpen(false)}
          preventOpenOnFocus
          placeholderText="DD-MM-YYYY"
          customInput={
            <CustomInput
              readOnly={readOnly}
              elemHeight={height}
              placeholder="DD-MM-YYYY"
              selectedDate={selectedDate}
              minDate={normalizedMinDate}
              maxDate={normalizedMaxDate}
              onDateTyped={(date) => onChange(date)}
              calendarOpen={calendarOpen}
            />
          }
          dateFormat="dd-MMM-yyyy"
          showYearDropdown
          showMonthDropdown
          scrollableMonthYearDropdown
          minDate={normalizedMinDate}
          maxDate={normalizedMaxDate}
          popperPlacement="top-start"
          popperProps={{
            strategy: "fixed",
            modifiers: [
              {
                name: "zIndex",
                enabled: true,
                phase: "write",
                fn: ({ state }) => {
                  if (state && state.styles && state.popper) {
                    state.styles.popper.zIndex = 9999;
                    state.styles.popper.overflow = "visible";
                  }
                  return state;
                },
              },
              {
                name: "preventOverflow",
                enabled: true,
                options: {
                  padding: 8,
                },
              },
            ],
          }}
          disabled={disabled}
        />
      </div>
      {error && <div className="dateErrorHeading">{error}</div>}
    </div>
  );
};

export default DateInput;
